// frontend/src/pages/admin/Users.jsx
import React, { useState, useEffect } from 'react';
import axios from '../../config/axios';
import { useAuth } from '../../contexts/AuthContext';
import { Ticket, Ban, Mail } from 'lucide-react';
import GrantTicketsModal from '../../components/admin/modals/GrantTicketsModal';
import BanUserModal from '../../components/admin/modals/BanUserModal';

const Users = () => {
    const { user: currentUser, updateUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showTicketsModal, setShowTicketsModal] = useState(false);
    const [showBanModal, setShowBanModal] = useState(false);
    const [verifyLoading, setVerifyLoading] = useState(null);

    const fetchUsers = async () => {
        try {
            const response = await axios.get('/admin/users');
            if (response.data.success) {
                setUsers(response.data.users);
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleGrantTickets = (user) => {
        setSelectedUser(user);
        setShowTicketsModal(true);
    };

    const handleBanUser = (user) => {
        setSelectedUser(user);
        setShowBanModal(true);
    };

    const handleTicketsSuccess = (updatedUser) => {
        setUsers(users.map(user =>
            user.id === updatedUser.id ? { ...user, tickets: updatedUser.tickets } : user
        ));
        if (updatedUser.id === currentUser?.id) {
            updateUser({ ...currentUser, tickets: updatedUser.tickets });
        }
    };

    const handleBanSuccess = (updatedUser) => {
        setUsers(users.map(user =>
            user.id === updatedUser.id
                ? { ...user, isBanned: updatedUser.isBanned, banExpiresAt: updatedUser.banExpiresAt }
                : user
        ));
    };

    const handleVerifyUser = async (userId) => {
        try {
            setVerifyLoading(userId);
            const response = await axios.post('/admin/users/verify-email', { userId });
            if (response.data.success) {
                setUsers(users.map(user =>
                    user.id === userId ? { ...user, emailVerified: new Date().toISOString() } : user
                ));
            } else {
                setError(response.data.message || 'Error al verificar el usuario');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Error al conectar con el servidor');
        } finally {
            setVerifyLoading(null);
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('es-AR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return <div className="flex justify-center items-center h-full text-gray-400">Cargando...</div>;
    }

    if (error) {
        return (
            <div className="p-4 rounded-lg" style={{ backgroundColor: '#2d1a1a' }}>
                <p className="text-red-400">{error}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold text-white">Gestión de Usuarios</h1>

            <div className="rounded-lg overflow-hidden" style={{ backgroundColor: '#0E2238' }}>
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead style={{ backgroundColor: '#0A1929' }}>
                            <tr>
                                {['Usuario', 'Email', 'Tickets', 'Registro', 'Estado', 'Acciones'].map(col => (
                                    <th key={col} className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400">
                                        {col}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr
                                    key={user.id}
                                    className="border-t"
                                    style={{
                                        borderColor: '#122436',
                                        backgroundColor: user.isBanned ? '#2d1a1a' : 'transparent'
                                    }}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div
                                                className="flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center"
                                                style={{ backgroundColor: '#2284DF' }}
                                            >
                                                <span className="text-white font-medium">
                                                    {user.username[0].toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-white">{user.username}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-300">{user.email}</div>
                                        {user.emailVerified ? (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-900 text-green-300">
                                                Verificado
                                            </span>
                                        ) : (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-900 text-yellow-300">
                                                Pendiente
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                                        {user.tickets}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                                        {formatDate(user.createdAt)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {user.isBanned ? (
                                            <div className="text-red-400 flex items-center">
                                                <Ban className="w-4 h-4 mr-1" />
                                                <span className="text-sm">
                                                    {user.banExpiresAt
                                                        ? `Baneado hasta ${formatDate(user.banExpiresAt)}`
                                                        : 'Ban permanente'}
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-900 text-green-300">
                                                Activo
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => handleGrantTickets(user)}
                                                className="text-gray-400 hover:text-yellow-400 transition-colors"
                                                title="Otorgar tickets"
                                                style={{ color: '#FCE503' }}
                                            >
                                                <Ticket className="w-5 h-5" />
                                            </button>
                                            {!user.emailVerified && (
                                                <button
                                                    onClick={() => handleVerifyUser(user.id)}
                                                    className="text-green-400 hover:text-green-300 transition-colors"
                                                    title="Verificar email manualmente"
                                                    disabled={verifyLoading === user.id}
                                                >
                                                    {verifyLoading === user.id ? (
                                                        <svg className="animate-spin w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                        </svg>
                                                    ) : (
                                                        <Mail className="w-5 h-5" />
                                                    )}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleBanUser(user)}
                                                className="text-red-400 hover:text-red-300 transition-colors"
                                                title={user.isBanned ? "Modificar ban" : "Banear usuario"}
                                            >
                                                <Ban className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showTicketsModal && selectedUser && (
                <GrantTicketsModal
                    user={selectedUser}
                    onClose={() => { setShowTicketsModal(false); setSelectedUser(null); }}
                    onSuccess={handleTicketsSuccess}
                />
            )}

            {showBanModal && selectedUser && (
                <BanUserModal
                    user={selectedUser}
                    onClose={() => { setShowBanModal(false); setSelectedUser(null); }}
                    onSuccess={handleBanSuccess}
                />
            )}
        </div>
    );
};

export default Users;
